// @ts-nocheck
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { CodeBlock } from "./code-block";
import { displayUserRole } from "@/lib/role-display";

interface Mention {
  userId?: string;
  claimId?: string;
  offset: number;
  length: number;
}

interface MentionUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  role?: string;
}

interface MarkdownMentionTextProps {
  text: string;
  mentions?: Mention[] | null;
  isOutgoing?: boolean;
  users?: MentionUser[];
}

export function MarkdownMentionText({ text, mentions, isOutgoing, users }: MarkdownMentionTextProps) {
  const proseClasses = `prose prose-sm dark:prose-invert max-w-none break-words
    prose-p:my-0 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5
    prose-headings:my-1 prose-pre:my-1 prose-code:text-xs
    prose-pre:bg-background/50 prose-pre:rounded
    ${isOutgoing ? "prose-invert" : ""}`;

  const markdownComponents = {
    a: ({ children, href, ...props }: any) => (
      <a href={href} target="_blank" rel="noopener noreferrer" className="underline break-all" {...props}>
        {children}
      </a>
    ),
    p: ({ children, ...props }: any) => (
      <span {...props}>{children}</span>
    ),
    pre: ({ children, ...props }: any) => <>{children}</>,
    code: ({ children, className, ...props }: any) => {
      const isBlock = className?.startsWith('language-');
      if (isBlock) {
        return <CodeBlock className={className}>{children}</CodeBlock>;
      }
      // Inline code — keep existing prose styling
      return <code className="text-xs bg-muted/80 rounded px-1 py-0.5 font-mono" {...props}>{children}</code>;
    },
  };

  if (!mentions || mentions.length === 0) {
    return (
      <span className={proseClasses}>
        <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkBreaks, remarkGfm]}>
          {text}
        </ReactMarkdown>
      </span>
    );
  }

  // Sort mentions by offset
  const sorted = [...mentions].sort((a, b) => a.offset - b.offset);
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  sorted.forEach((mention, i) => {
    // Text before mention -> render through markdown
    if (mention.offset > lastIndex) {
      const segment = text.slice(lastIndex, mention.offset);
      parts.push(
        <span key={`md-${i}`} className={proseClasses}>
          <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkBreaks, remarkGfm]}>
            {segment}
          </ReactMarkdown>
        </span>
      );
    }
    // Mention span -> render as clickable chip with popover if user data available
    const mentionText = text.slice(mention.offset, mention.offset + mention.length);

    // Claim mention -> render as external link
    if (mention.claimId) {
      parts.push(
        <a
          key={`claim-${i}`}
          href={`/claims/${mention.claimId}`}
          target="_blank"
          rel="noreferrer"
          className={`font-medium rounded px-1 hover:underline cursor-pointer ${
            isOutgoing
              ? 'bg-card/20 text-white hover:bg-card/30'
              : 'bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400'
          }`}
          data-testid={`claim-mention-${mention.claimId}`}
        >
          {mentionText}
        </a>
      );
      lastIndex = mention.offset + mention.length;
      return;
    }

    const user = users?.find(u => u.id === mention.userId);
    const userName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : null;
    if (user?.email) {
      parts.push(
        <Popover key={`mention-${i}`}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={`font-medium rounded px-1 underline cursor-pointer border-none ${isOutgoing ? "bg-card/20 text-white hover:bg-card/30" : "bg-primary/10 text-primary hover:bg-primary/20"}`}
              data-testid={`mention-${mention.userId}`}
            >
              {mentionText}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-3" side="top">
            <div className="space-y-1">
              {userName && <p className="text-sm font-medium">{userName}</p>}
              <p className="text-xs text-muted-foreground">{user.email}</p>
              {user.role && <Badge variant="outline" className="text-xs">{displayUserRole(user.role)}</Badge>}
            </div>
          </PopoverContent>
        </Popover>
      );
    } else {
      parts.push(
        <span
          key={`mention-${i}`}
          className={`font-medium rounded px-0.5 ${isOutgoing ? "bg-card/25 text-white" : "bg-primary/20 text-primary"}`}
          data-testid={`mention-${mention.userId}`}
        >
          {mentionText}
        </span>
      );
    }
    lastIndex = mention.offset + mention.length;
  });

  // Remaining text
  if (lastIndex < text.length) {
    const segment = text.slice(lastIndex);
    parts.push(
      <span key="md-end" className={proseClasses}>
        <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkBreaks, remarkGfm]}>
          {segment}
        </ReactMarkdown>
      </span>
    );
  }

  return <span className="markdown-mention-text">{parts}</span>;
}
